/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { CategoryHierarchy, IProductRefinerHierarchy, IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import MsDyn365, { getCatalogId, ICoreContext } from '@msdyn365-commerce/core';
import { format, ProductPrice, ProductRefinerValue, SortColumn, TextValueTranslation } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    DimensionAvailabilitiesForProductSearchResultInput,
    DimensionTypes,
    getDimensionAvailabilitiesForProductSearchResultAction,
    getPriceForProductSearchResult,
    IDimensionsApp,
    PriceForProductSearchResultInput
} from '@msdyn365-commerce-modules/retail-actions';
import {
    getTelemetryObject,
    ILabeledDropdownOption,
    IModuleProps,
    INodeProps,
    isMobile,
    ITelemetryContent,
    LabeledDropdown,
    UncontrolledPagination,
    VariantType
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, observable, reaction, transaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import {
    buildListPageUrl,
    getCollectionProducts,
    getCollectionRefinersAction,
    getCurrentUrl,
    GetFullProductsByCollectionInput,
    IFullProductsSearchResultsWithCount,
    parseQueryParam,
    /*ProductListInventoryFilteringOptions,*/ RefinersByCollectionInput,
    sortOptions
} from './actions';
import { hydrateRefinersFromUrl } from './actions/url-utils';
import {
    ErrorMessage,
    FeatureSearchResultItem,
    getUpdatedRefinementCriteria,
    IChoiceSummaryClickNotification,
    IRefineItemCommonProps,
    IRefineItemToggleNotification,
    ISearchResultModalViewProps,
    isMatchingRefinementCriterion,
    Link,
    ProductSearchResultItems,
    SearchResultModal,
    Separator,
    Title
} from './components';
import ChoiceSummary from './components/choice-summary';
import RefineSubmenu from './components/refine-submenu';
import { ListPageState } from './list-page-state';
import { IEshopSearchResultContainerData } from './eshop-search-result-container.data';
import { expandRefinersCount, IEshopSearchResultContainerProps } from './eshop-search-result-container.props.autogenerated';
import Dropdown from 'react-bootstrap/Dropdown';
import DropdownButton from 'react-bootstrap/DropdownButton';

export interface ISearchResultContainerViewProps extends IEshopSearchResultContainerProps<IEshopSearchResultContainerData> {
    products?: React.ReactNode;
    className?: string;
    SearchResultContainer: IModuleProps;
    TitleViewProps: ITitleViewProps;
    categoryHierarchy: ICategoryHierarchyViewProps;
    pagination?: React.ReactNode;
    ProductsContainer: INodeProps;
    ProductSectionContainer: INodeProps;
    refineMenu: IRefineMenuViewProps;
    sortByOptions: ISortByViewProps;
    choiceSummary?: React.ReactNode;
    modalToggle: React.ReactNode;
    searchResultModal: ISearchResultModalViewProps;
    isMobile: boolean;
    CategoryNavContainer: INodeProps;
    RefineAndProductSectionContainer: INodeProps;
    errorMessage: React.ReactNode;
    FeatureSearchContainer: INodeProps;
    similarLookProduct?: React.ReactNode;
    productPrices?: ProductPrice[];
}

export interface ITitleViewProps {
    TitleContainer: INodeProps;
    title: ISearchResultTitle;
}

export interface IRefineMenuViewProps {
    RefineMenuContainer: INodeProps;
    RefinerSectionContainer: INodeProps;
    refiners?: React.ReactNode[];
}

export interface ICategoryHierarchyViewProps {
    categoryHierarchyList?: React.ReactNode[];
    categoryHierarchySeparator?: React.ReactNode;
    CategoryHierarchyContainer: INodeProps;
}

export interface ISortByViewProps {
    SortingContainer: INodeProps;
    sortByDropDown?: React.ReactNode;
    sortByRadio?: React.ReactNode;
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Title component for search result container.
 */
export interface ISearchResultTitle {
    titlePrefix?: React.ReactNode;
    titleText?: React.ReactNode;
    titleCount?: React.ReactNode;
}

export interface ISearchResultContainerState {
    sortingState: ISortByCollectionState;
    modalIsOpen: boolean;
    refiners: IProductRefinerHierarchy[];
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
}

interface ISortByCollectionState {
    selectedSortByOption: ILabeledDropdownOption;
    pending: boolean;
}

/**
 *
 * SearchResultContainer component.
 * @extends {React.PureComponent<IEshopSearchResultContainerProps<IEshopSearchResultContainerData>>}
 */
@observer
export default class SearchResultContainer extends React.PureComponent<
    IEshopSearchResultContainerProps<IEshopSearchResultContainerData>,
    ISearchResultContainerState
> {
    @computed get isMobile(): boolean {
        return this._viewport === 'xs' || this._viewport === 'sm';
    }

    private readonly sortByDropdownOptions: ILabeledDropdownOption[] = [
        {
            key: sortOptions.sortByOptionRelevanceDesc,
            value: this.props.resources.sortByOptionRelevanceDesc
        },
        {
            key: sortOptions.sortByOptionPriceAsc,
            value: this.props.resources.sortByOptionPriceAsc
        },
        {
            key: sortOptions.sortByOptionPriceDesc,
            value: this.props.resources.sortByOptionPriceDesc
        }
    ];

    private readonly _refineItemCommonProps: IRefineItemCommonProps;

    private readonly _pageType: string | undefined = this.props.context.request.urlTokens.pageType;

    @observable
    private _viewport: GridSize = 'lg';

    private readonly _modalToggleRef: React.RefObject<HTMLButtonElement>;

    private readonly _sortAndFilterContainerRef: React.RefObject<LabeledDropdown>;

    private expandrefinerCount: number | undefined = 0;

    private readonly telemetryContent: ITelemetryContent;

    private readonly productPrices: ProductPrice[];

    private readonly defaultPageNumber = 0;

    private readonly catalogId = getCatalogId(this.props.context.request);

    private isListPageStateUpdated: boolean = false;

    public constructor(props: IEshopSearchResultContainerProps<IEshopSearchResultContainerData>, state: ISearchResultContainerState) {
        super(props);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Check required.
        this._viewport = props.context.request?.device?.Type === 'Mobile' ? 'xs' : 'lg';
        this._modalToggleRef = React.createRef<HTMLButtonElement>();
        this._sortAndFilterContainerRef = React.createRef<LabeledDropdown>();
        this._toggleModal = this._toggleModal.bind(this);
        this._updateViewport = this._updateViewport.bind(this);
        this.productPrices = [];
        this.state = {
            sortingState: {
                pending: false,
                selectedSortByOption: this.sortByDropdownOptions[0]
            },
            modalIsOpen: false,
            refiners: []
        };
        const { placeholderTextMax, minLabel, maxLabel, rangeNameFormat } = this.props.resources;

        const locale = this.props.context.request.locale;
        const telemetry = this.props.telemetry;
        const validationErrorNaN = this.props.resources.validationErrorNotNumber;
        const validationErrorRange = this.props.resources.validationErrorNotRange;
        this._refineItemCommonProps = {
            telemetry,
            locale,
            placeholderTextMax,
            minLabel,
            maxLabel,
            rangeNameFormat,
            validationErrorNaN,
            validationErrorRange
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this._updateViewport();
    }

    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && ArrayExtensions.hasElements(nameTranslations)) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation?.Text;
    }

    public async componentDidMount(): Promise<void> {
        if (MsDyn365.isBrowser && window.addEventListener) {
            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();
        }

        const listPageState = await this.props.data.listPageState;
        const products = await this.props.data.products;

        this._updateListPageState(listPageState, products);

        // Initialize reaction based on listPageState properties
        reaction(
            () => {
                return [
                    listPageState.activeFilters?.length,
                    listPageState.currentPageNumber,
                    ArrayExtensions.hasElements(listPageState.sortingCritera?.Columns)
                ];
            },
            async () => {
                const newListPageState = await this.props.data.listPageState;
                const input = new GetFullProductsByCollectionInput(
                    newListPageState.pageType,
                    this.props.context.request.apiSettings,
                    {
                        Paging: {
                            Top: this.props.config.itemsPerPage,
                            Skip: Math.max(newListPageState.pageSize * (newListPageState.currentPageNumber ?? 0), 0)
                        },
                        count: true,
                        Sorting: newListPageState.sortingCritera ?? {}
                    },
                    newListPageState.activeFilters ?? [],
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- RecordId is string. Check required.
                    +(this.props.context.request.urlTokens.recordId || 0),

                    // eslint-disable-next-line max-len -- existing code
                    this.props.context.request.query?.q
                        ? this.props.context.request.query.q
                        : JSON.stringify({
                              ProductId: this.props.context.request.query?.productId,
                              Recommendation: this.props.context.request.query?.recommendation
                          }),
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Required check.
                    !!this.props?.config?.includeAttributes,
                    this.props.config.updateRefinerPanel,
                    this.props.context.request.locale,
                    this.catalogId
                );

                const productResults = await getCollectionProducts(input, this.props.context.actionContext);
                newListPageState.totalProductCount = productResults.count;
                newListPageState.activeProducts = productResults.products;

                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- check is required.
                if (
                    this.props.config.enableAffiliationBasedPricing &&
                    this.props.context.actionContext.requestContext?.user?.isAuthenticated
                ) {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Call api to retrieve the availabilities data without blocking the UI.
                    this._getActivePrice();
                }
                if (this.props.config.updateRefinerPanel) {
                    const refinerInput = new RefinersByCollectionInput(
                        newListPageState.pageType,
                        this.props.context.request.apiSettings,
                        {
                            Paging: {
                                Top: this.props.config.itemsPerPage,
                                Skip: Math.max(
                                    newListPageState.pageSize * (newListPageState.currentPageNumber ?? this.defaultPageNumber),
                                    this.defaultPageNumber
                                )
                            },
                            count: true,
                            Sorting: newListPageState.sortingCritera ?? {}
                        },
                        newListPageState.activeFilters ?? [],
                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- RecordId is string. Check required.
                        +(this.props.context.request.urlTokens.recordId || 0),
                        this.props.context.request.query?.q
                            ? this.props.context.request.query.q
                            : this.props.context.request.query?.productId,
                        false,
                        this.props.config.updateRefinerPanel,
                        undefined,
                        this.catalogId
                    );

                    const refiners = await getCollectionRefinersAction(refinerInput, this.props.context.actionContext);
                    this.setState({ refiners });
                }
            }
        );

        if (
            this.props.config.enableAffiliationBasedPricing &&
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Condition required.
            this.props.context.actionContext.requestContext?.user?.isAuthenticated
        ) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Call api to retrieve the availabilities data without blocking the UI.
            this._getActivePrice();
        }

        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = context.app.config.dimensionsInProductCard;
        const dimensionsToDisplayAsSwatch = context.app.config.dimensionsAsSwatchType;
        if (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            ArrayExtensions.hasElements(dimensionsToDisplayAsSwatch) &&
            !dimensionsToDisplayAsSwatch.includes(DimensionTypes.none) &&
            this.props.config.includeAttributes
        ) {
            await this._getProductsDimensionsAvailabilities();
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser && window.removeEventListener) {
            window.removeEventListener('resize', this._updateViewport);
        }
    }

    public render(): JSX.Element {
        const {
            config: { imageSettings, className },
            resources,
            data: { listPageState, products: productCollection, featureProduct }
        } = this.props;

        if (!this.isListPageStateUpdated && listPageState.result && productCollection.result) {
            this._updateListPageState(listPageState.result, productCollection.result);
            this.isListPageStateUpdated = true;
        }

        const products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const searchProduct = listPageState.result?.featureProduct ?? featureProduct.result ?? null;
        let errorText = '';
        if (!ArrayExtensions.hasElements(products)) {
            errorText = this._pageType === 'Category' ? resources.resultCategoryNotFoundText : resources.resultSearchNotFoundText;
        }
        const productsComponent = this._getProducts();
        const featureComponent = (
            <FeatureSearchResultItem
                product={searchProduct}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                telemetryContent={this.telemetryContent}
                recommendation={this.props.context.request.query?.recommendation}
            />
        );

        const searchResultContainerViewProps = {
            ...this.props,
            products: productsComponent,
            TitleViewProps: this._getCollectionTitle(),
            categoryHierarchy: !this.props.config.disableHierarchy && this._getCategoryHierarchy(),
            refineMenu: this._getRefineMenu(),
            className: classnames('ms-search-result-container', className),
            SearchResultContainer: {
                moduleProps: this.props,
                className: classnames('ms-search-result-container', className)
            },
            sortByOptions: this.props.data.listPageState.result?.totalProductCount !== 0 ? this._getSortingDropDown() : null,
            pagination: this._getPagination(),
            ProductsContainer: { className: 'ms-search-result-container__Products' },
            ProductSectionContainer: {
                className: 'ms-search-result-container__product-section'
            },
            CategoryNavContainer: {
                className: 'ms-search-result-container__category-nav-section'
            },
            RefineAndProductSectionContainer: {
                className: 'ms-search-result-container__refine-product-section'
            },
            choiceSummary: this._getChoiceSummary(),
            modalToggle:
                this.props.data.listPageState.result?.totalProductCount !== 0 ? (
                    <svg
                        id='search-result-modal'
                        className='bi bi-filter filter-icon'
                        aria-label={resources.modalTitle}
                        onClick={this._toggleModal}
                        width='1em'
                        height='1em'
                        viewBox='0 0 16 16'
                        fill='currentColor'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <path
                            fillRule='evenodd'
                            d='M6 10.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z'
                        />
                    </svg>
                ) : null,
            searchResultModal: this._getSearchResultModal(),
            isMobile: this.isMobile,
            errorMessage: errorText && <ErrorMessage text={errorText} />,
            FeatureSearchContainer: { className: 'ms-feature-search-result' },
            similarLookProduct: searchProduct ? featureComponent : null,
            productPrices: this.productPrices
        };
        return this.props.renderView(searchResultContainerViewProps) as React.ReactElement;
    }

    /**
     * Update the list page state with initial data.
     * @param listPageState -- List page State.
     * @param products -- Products.
     */
    private readonly _updateListPageState = (listPageState: ListPageState, products: IFullProductsSearchResultsWithCount): void => {
        let querySorting: SortColumn[] = [];
        const defaultPageNumber: number = 10;

        if (this._pageType === 'Category') {
            listPageState.pageType = 'Category';
        } else {
            listPageState.pageType = 'Search';
        }

        if (this.props.context.request.query?.sorting) {
            querySorting = JSON.parse(decodeURIComponent(this.props.context.request.query.sorting)) as SortColumn[];
        }

        listPageState.currentPageNumber =
            (this.props.context.request.query &&
                +this.props.context.request.query.skip / (this.props.config.itemsPerPage ?? defaultPageNumber)) ||
            0;
        listPageState.sortingCritera = { Columns: querySorting };
        listPageState.pageSize = this.props.config.itemsPerPage ?? defaultPageNumber;
        listPageState.activeProducts = products.products;
        listPageState.totalProductCount = products.count;
        listPageState.activeFilters = hydrateRefinersFromUrl(this.props.context.request);
        if (this.props.context.request.query?.productId) {
            const featureProduct = this.props.data.featureProduct.result;
            if (featureProduct) {
                listPageState.featureProduct = featureProduct;
            }
        }
    };

    /**
     * Get products dimension availabilities.
     * @returns Void.
     */
    private readonly _getProductsDimensionsAvailabilities = async (): Promise<void> => {
        const productsList = this.props.data.listPageState.result?.activeProducts ?? [];

        const productsDimensionAvailabilities = await getDimensionAvailabilitiesForProductSearchResultAction(
            new DimensionAvailabilitiesForProductSearchResultInput(productsList, this.props.context as ICoreContext<IDimensionsApp>),
            this.props.context.actionContext
        );

        if (ArrayExtensions.hasElements(productsDimensionAvailabilities)) {
            this.setState({ productsDimensionAvailabilities });
        }
    };

    /**
     * Get the active price for the products.
     */
    private readonly _getActivePrice = async (): Promise<void> => {
        const productsList = this.props.data.listPageState.result?.activeProducts ?? [];
        const products = await getPriceForProductSearchResult(
            new PriceForProductSearchResultInput(productsList),
            this.props.context.actionContext
        );

        const listPageState = await this.props.data.listPageState;
        listPageState.activeProducts = products;
    };

    private readonly _getSearchResultModal = (): ISearchResultModalViewProps => {
        const { resources } = this.props;
        return SearchResultModal({
            resources: {
                modalCloseButtonText: resources.modalCloseButtonText,
                modalTitle: resources.modalTitle
            },
            isOpen: this.state.modalIsOpen,
            returnRef: this._modalToggleRef,
            onModalToggle: this._toggleModal
        });
    };

    private readonly _getCollectionTitle = (): ITitleViewProps => {
        const { data, context, resources } = this.props;

        let collectionTitle: string | undefined = '';
        if (context && context.request && context.request.query && context.request.query.q) {
            collectionTitle = `"${context.request.query.q}"`;
        } else {
            collectionTitle =
                (data.category.result &&
                    SearchResultContainer.getFriendlyName(context.request.locale, data.category.result.NameTranslations)) ||
                (data.category.result && data.category.result.Name);
        }
        let productCountText = '';
        let productCountNumber: number | undefined;
        if (data.listPageState && data.listPageState.result && data.listPageState.result.totalProductCount !== undefined) {
            productCountNumber = data.listPageState.result.totalProductCount;
        } else if (data.products && data.products.result) {
            productCountNumber = data.products.result.count;
        }

        if (productCountNumber && productCountNumber !== 0) {
            productCountText =
                productCountNumber !== 1
                    ? format(this.props.resources.numberOfProducts, productCountNumber)
                    : this.props.resources.oneProduct;
        } else {
            productCountText = format(this.props.resources.numberOfProducts, 0);
        }
        const pageName = context.request.telemetryPageName ? `result-count-${context.request.telemetryPageName.toLowerCase()}` : '';
        const titlePrefix = <Title className='ms-search-result__collection-title-prefix' text={resources.searchTextPrefix} />;
        const titleText = collectionTitle && <Title className='ms-search-result__collection-title-text' text={collectionTitle} />;
        const titleCount = <Title className='ms-search-result__collection-title-count' text={`(${productCountText})`} />;

        return {
            TitleContainer: {
                className: `ms-search-result-container__title ${pageName}`
            },
            title: {
                titlePrefix,
                titleText,
                titleCount
            }
        };
    };

    private readonly _getCategoryHierarchy = (): ICategoryHierarchyViewProps => {
        const { data } = this.props;
        const categoryHierarchy = data.categoryHierarchy.result;
        const categoryLinks: JSX.Element[] = [];
        if (categoryHierarchy && categoryHierarchy.length === 3) {
            categoryLinks.push(
                <Link
                    key='1'
                    text={categoryHierarchy[1].Name}
                    ariaLabel={`${this.props.resources.categoryLinkAriaLabel} ${categoryHierarchy[1].Name}`}
                    href={categoryHierarchy[1].Url}
                />
            );
            categoryLinks.push(<span key='2'>{categoryHierarchy[2].Name}</span>);
        }

        const categoryLinkSeparator = <Separator separator='>' />;

        return {
            CategoryHierarchyContainer: {
                tag: 'nav',
                className: 'ms-search-result-container__category-hierarchy'
            },
            categoryHierarchyList: categoryLinks,
            categoryHierarchySeparator: categoryLinkSeparator
        };
    };

    private readonly _getSortingDropDown = (): ISortByViewProps => {
        const { resources } = this.props;
        const activeDropdown = this._getCurrentlySelectedOption() || this.state.sortingState.selectedSortByOption;
        let dropdownOptions = this._getAllSortByOptions();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- App config settings are of "any" value.
        if (this.props.context.app.config.hideRating) {
            dropdownOptions = dropdownOptions.filter(dropdownOption => dropdownOption.key !== sortOptions.sortByOptionRatingDesc);
        }

        const dropdown = (
            <>
                <label className='msc-l-dropdown__label reviews-list-sort-by' htmlFor='categorySortByDropdown'>
                    {resources.sortByDropdownLabel}
                </label>
                <DropdownButton id='categorySortByDropdown' title={activeDropdown.value} onSelect={this._updateSortByDropdown}>
                    {dropdownOptions.map(o => (
                        <Dropdown.Item key={o.key} eventKey={o.value}>
                            {o.value}
                        </Dropdown.Item>
                    ))}
                </DropdownButton>
            </>
        );

        const radio = (
            <>
                <label style={{ fontWeight: 'bold' }}>{resources.sortByDropdownLabel}</label>
                <div onChange={this._updateSortByRadio}>
                    {dropdownOptions.map(option => {
                        const checked = option.key === activeDropdown.key;
                        return (
                            <>
                                <input
                                    key={option.key}
                                    checked={checked}
                                    aria-checked={checked}
                                    type='radio'
                                    id={option.key}
                                    name='categorySortBy'
                                    value={option.value}
                                />
                                <label style={{ margin: '0.5rem' }} htmlFor={option.key}>
                                    {option.value}
                                </label>
                                <br />
                            </>
                        );
                    })}
                </div>
            </>
        );
        return {
            SortingContainer: {
                className: 'ms-search-result-container__Sort-by-category'
            },
            sortByDropDown: dropdown,
            sortByRadio: radio
        };
    };

    private readonly _getPagination = (): React.ReactNode => {
        const { config, context, data, resources } = this.props;
        const listPageState = data.listPageState.result;
        const fullUrl = getCurrentUrl(context.request);
        const itemsPerPage = config.itemsPerPage || 10;
        // eslint-disable-next-line max-len -- existing code
        const skipCount = listPageState?.currentPageNumber
            ? listPageState.currentPageNumber * (this.props.config.itemsPerPage || 10)
            : (this.props.context.request.query && +this.props.context.request.query.skip) || 0;
        const totalItems = listPageState?.totalProductCount || 0;
        const previousText = resources.flipperPrevious;
        const nextText = resources.flipperNext;

        if (totalItems <= itemsPerPage) {
            return null;
        }
        return (
            <UncontrolledPagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={resources.paginationAriaLabel}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                telemetryContent={this.telemetryContent}
            />
        );
    };

    private readonly _getRefineMenu = (): IRefineMenuViewProps => {
        const { data, context, config } = this.props;
        const categoryHierarchy = data.categoryHierarchy.result;
        if (categoryHierarchy && categoryHierarchy.length > 0) {
            const childCategories = categoryHierarchy[`${categoryHierarchy.length - 1}`].Children;
            if (childCategories && childCategories.length > 0) {
                return {
                    RefineMenuContainer: {
                        className: 'ms-search-result-container__refine-menu'
                    },
                    RefinerSectionContainer: {
                        className: 'ms-search-result-container__refiner-section'
                    },
                    refiners: [
                        <div className='ms-refine-submenu list-group Category' key='refine-menu-category'>
                            <div className='ms-refine-submenu__toggle_expanded'>{config.categoriesLabel}</div>
                            <ul className='ms-refine-submenu__list Category'>
                                {childCategories.map((c: CategoryHierarchy, i: number) => (
                                    <li className='ms-refine-submenu-item' key={i}>
                                        <a className='ms-refine-submenu-item-category-link' href={c.Url}>
                                            {c.Name}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ]
                };
            }
        }

        const tempRangeTypeTODO = context.request.query && context.request.query.inputRange ? 'input' : 'slider';
        let validRefiners =
            this.props.config.updateRefinerPanel && ArrayExtensions.hasElements(this.state.refiners)
                ? this.state.refiners.filter(refiner => {
                      return ArrayExtensions.hasElements(refiner.Values); //&& !this._shouldHideFromRefiners(refiner);
                  })
                : data.refiners.result &&
                  data.refiners.result.filter(refiner => {
                      return ArrayExtensions.hasElements(refiner.Values); //&& !this._shouldHideFromRefiners(refiner);
                  });
        validRefiners = validRefiners?.filter(r => r.IsDimension);

        const activeRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        this._getexpandRefinerCount(validRefiners && validRefiners.length, config.expandRefinersCount);
        const expandedRefiners = this.expandrefinerCount;
        const subMenus =
            validRefiners &&
            validRefiners.map((productRefinerHierarchy: IProductRefinerHierarchy, index: number) => {
                return (
                    <RefineSubmenu
                        productRefinerHierarchy={productRefinerHierarchy}
                        selectedRefinerValues={activeRefiners}
                        refineItemCommonProps={this._refineItemCommonProps}
                        minValueSliderThumbAriaLabel={this.props.resources.minValueSliderThumbAriaLabel}
                        maxValueSliderThumbAriaLabel={this.props.resources.maxValueSliderThumbAriaLabel}
                        key={index}
                        onUpdateRefiners={this._onUpdateRefiners}
                        urlBuilder={this._buildRefinerUrl}
                        isDisabled={false}
                        isExpandedOnInitialLoad={!!(expandedRefiners && expandedRefiners > index)}
                        tempRangeTypeTODO={tempRangeTypeTODO}
                        context={context}
                        moduleId={this.props.id}
                        moduleTypeName={this.props.typeName}
                        telemetryContent={this.telemetryContent}
                        productCountAriaLabel={this.props.resources.productCountAriaLabel}
                        refineItemAriaLabel={this.props.resources.refineItemAriaLabel}
                        refineItemsAriaLabel={this.props.resources.refineItemsAriaLabel}
                        singleProductCountAriaLabel={this.props.resources.singleProductCountAriaLabel}
                        isMobile={this.isMobile}
                    />
                );
            });

        return {
            RefineMenuContainer: {
                className: 'ms-search-result-container__refine-menu'
            },
            RefinerSectionContainer: {
                className: 'ms-search-result-container__refiner-section'
            },
            refiners: subMenus
        };
    };

    /**
     * Should hide from refiners.
     * @param refiner - The refiner.
     * @returns ShouldHideFromRefiners.
     */
    // private readonly _shouldHideFromRefiners = (refiner: IProductRefinerHierarchy): boolean => {
    //     // Always hide sortable attribute since it's just for sorting products in Azure search, not for customers
    //     return (
    //         refiner.RecordId === this.props.data.products.result?.inventoryAwareSortableAttributeId ||
    //         // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app configs are of generic type
    //         (this.props.context.app.config.productListInventoryDisplay === ProductListInventoryFilteringOptions.HideOOS &&
    //             refiner.RecordId === this.props.data.products.result?.channelInventoryConfigurationId)
    //     );
    // };

    private readonly _getChoiceSummary = (): React.ReactNode => {
        const { resources, data, telemetry } = this.props;
        const selectedRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];
        return (
            <ChoiceSummary
                classNames='ms-choice-summary-by-category'
                clearAllText={resources.clearAllText}
                label={resources.choiceSummaryLabel}
                closeAriaLabel={resources.closeAriaLabel}
                selectedChoices={selectedRefiners}
                // @ts-expect-error: NOTE Type-unsafe line below, null refinersByCategoryHierarchy case not handled
                refinerHierarchy={data.refiners.result}
                choiceFormat={resources.choiceFormat}
                choiceRangeValueFormat={resources.choiceRangeValueFormat}
                telemetry={telemetry}
                onChoiceClicked={this._onChoiceClicked}
                urlBuilder={this._buildRefinerUrlForChoiceSummary}
                choiceAriaLabel={resources.choiceAriaLabel}
                telemetryContent={this.telemetryContent}
                context={this.props.context}
                channelInventoryConfigurationId={this.props.data.products.result?.channelInventoryConfigurationId}
            />
        );
    };

    /**
     * Get Active products.
     * @returns - JSX Elements.
     */
    private readonly _getProducts = (): JSX.Element => {
        const {
            config: { imageSettings, allowBackNavigation: shouldAllowBackNavigation }
        } = this.props;
        const { resources } = this.props;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- Reading config value.
        const breadCrumbType: string = this.props.context.app.config.breadcrumbType;
        const shouldAllowBack = shouldAllowBackNavigation && (breadCrumbType === 'back' || breadCrumbType === 'categoryAndBack');

        const products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const quickviewSlot = this.props.slots?.quickview?.[0];

        const isPriceMinMaxFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );
        return (
            <ProductSearchResultItems
                products={products}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                allowBackNavigation={shouldAllowBack}
                telemetryContent={this.telemetryContent}
                quickviewSlot={quickviewSlot}
                channelInventoryConfigurationId={this.props.data.products.result?.channelInventoryConfigurationId}
                isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                productsDimensionAvailabilities={this.state.productsDimensionAvailabilities}
                eShopPdpUrl={this.props.config.eshopPdpUrl}
            />
        );
    };

    private readonly _updateSortByDropdown = (event: any): void => {
        const {
            context: { request: requestContext, actionContext }
        } = this.props;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            // eslint-disable-next-line max-len -- existing code
            const error = `[sort-by-category] cannot sort without context: ${!requestContext ? 'requestContext ' : ''} ${
                !actionContext ? 'actionContext ' : ''
            } could not be found`;
            this.props.telemetry.warning(error);
            return;
        }

        const selectedOption = this.sortByDropdownOptions.filter(o => o.value === event)[0];
        const sortColumns = [this._getSortColumnFromSelectedOption(selectedOption)];
        window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), undefined, sortColumns, undefined));
        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.sortingCritera = {
                Columns: sortColumns
            };
        });
    };

    private readonly _updateSortByRadio = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const {
            context: { request: requestContext, actionContext }
        } = this.props;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            // eslint-disable-next-line max-len -- existing code
            const error = `[sort-by-category] cannot sort without context: ${!requestContext ? 'requestContext ' : ''} ${
                !actionContext ? 'actionContext ' : ''
            } could not be found`;
            this.props.telemetry.warning(error);
            return;
        }

        const selectedOption = this.sortByDropdownOptions.filter(o => o.value === event.target.value)[0];
        const sortColumns = [this._getSortColumnFromSelectedOption(selectedOption)];
        window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), undefined, sortColumns, undefined));
        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.sortingCritera = {
                Columns: sortColumns
            };
        });
    };

    private readonly _getSortColumnFromSelectedOption = (option: ILabeledDropdownOption): SortColumn => {
        const { data } = this.props;

        if (!data.searchConfiguration.result) {
            return {};
        }

        const mappedConfiguration = data.searchConfiguration.result.find(searchConfiguration => {
            return searchConfiguration.key === option.key;
        });

        if (mappedConfiguration) {
            return mappedConfiguration.sortColumn;
        }

        return {};
    };

    /**
     * Retrieves current sort criteria.
     * @returns Sort criteria column which is currently active or undefined if it's not specified.
     */
    private readonly _getSortCriteriaColumn = (): SortColumn | undefined => {
        const sortCriteria = this.props.data.listPageState.result?.sortingCritera;
        if (ArrayExtensions.hasElements(sortCriteria?.Columns)) {
            return sortCriteria!.Columns[0];
        }

        const queryColumns = parseQueryParam<SortColumn[]>('sorting', this.props.context.request);
        if (ArrayExtensions.hasElements(queryColumns)) {
            return queryColumns[0];
        }

        return undefined;
    };

    /**
     * Retrieves dropdown options.
     * @returns All dropdown options.
     */
    private readonly _getAllSortByOptions = (): ILabeledDropdownOption[] => {
        const searchConfiguration = this.props.data.searchConfiguration.result;

        if (!searchConfiguration) {
            return [];
        }

        const mappedSearchConfiguration = this.sortByDropdownOptions.filter(mappedSearchConfig =>
            searchConfiguration.find(item => mappedSearchConfig.key === item.key)
        );

        return ArrayExtensions.validValues(mappedSearchConfiguration);
    };

    /**
     * Retrieves current selected dropdown option based on the sort criteria.
     * @returns Current dropdown option.
     */
    private readonly _getCurrentlySelectedOption = (): ILabeledDropdownOption | undefined => {
        const activeSortColumn = this._getSortCriteriaColumn();

        const searchConfiguration = this.props.data.searchConfiguration.result;

        if (!activeSortColumn || !searchConfiguration) {
            return undefined;
        }

        const activeMappedConfig = searchConfiguration.find(
            mappedSearchConfig =>
                mappedSearchConfig.sortColumn.ColumnName === activeSortColumn.ColumnName &&
                mappedSearchConfig.sortColumn.IsDescending === activeSortColumn.IsDescending
        );
        if (!activeMappedConfig) {
            return undefined;
        }

        return this.sortByDropdownOptions.find(dropdownOption => dropdownOption.key === activeMappedConfig.key);
    };

    private readonly _onUpdateRefiners = (itemToggleNotification: IRefineItemToggleNotification): void => {
        const {
            context: { request: requestContext, actionContext }
        } = this.props;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            const error = `Refine menu cannot refine search criteria: 
                ${!requestContext ? 'requestContext ' : ''} ${!actionContext ? 'actionContext ' : ''} could not be found`;
            this.props.telemetry.warning(error);
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Server side the list PageState will not be called.
        const updatedRefinementCriteria = getUpdatedRefinementCriteria(
            itemToggleNotification,
            this.props.data.listPageState.result?.activeFilters || []
        );

        window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), updatedRefinementCriteria));
        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.activeFilters = updatedRefinementCriteria;
        });
    };

    private readonly _buildRefinerUrl = (itemToggleNotification: IRefineItemToggleNotification): string => {
        if (this.props.data.listPageState.result) {
            const newRefinementCriteria = getUpdatedRefinementCriteria(
                itemToggleNotification,
                this.props.data.listPageState.result.activeFilters || []
            );

            return buildListPageUrl(getCurrentUrl(this.props.context.request), newRefinementCriteria);
        }
        this.props.telemetry.warning('[buildRefinerQueryString]List Page State Not available, unable to build refiner URL');
        return '';
    };

    private readonly _onChoiceClicked = (notification: IChoiceSummaryClickNotification): void => {
        const { data, telemetry } = this.props;

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._onChoiceClicked]ListPageState unavailable, unable to update refiners.');
            return;
        }

        let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        if (!notification.clearAll) {
            const selectedChoice = notification.choiceClicked;
            if (!selectedChoice) {
                telemetry.warning('[choice-summary-by-category._onChoiceClicked] Choice could not be determined');
                return;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedChoice, selectedCriterion)
            );
        } else {
            currentRefinementCriteria = [];

            // Set focus to sort and filter
            const dropdownElementId = this._sortAndFilterContainerRef.current && this._sortAndFilterContainerRef.current.props.dropdownId;
            const dropdownElement = dropdownElementId && document.getElementById(dropdownElementId);
            setTimeout(() => {
                dropdownElement && dropdownElement.focus();
            }, 50);
        }

        const fullUrl = getCurrentUrl(this.props.context.request);
        window.history.pushState({}, '', buildListPageUrl(fullUrl, currentRefinementCriteria));
        data.listPageState.result.currentPageNumber = 0;
        data.listPageState.result.activeFilters = currentRefinementCriteria;
    };

    private readonly _buildRefinerUrlForChoiceSummary = (selectedRefiner: ProductRefinerValue, isClearAll: boolean): string => {
        const { data, telemetry } = this.props;
        const fullUrl = getCurrentUrl(this.props.context.request);

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._buildRefinerUrl]ListPageState unavailable, unable to create refiner URL.');
            return fullUrl.href;
        }

        let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        if (!isClearAll) {
            if (!selectedRefiner) {
                telemetry.warning('[choice-summary-by-category._buildRefinerUrl] URL for Choice could not be determined');
                return fullUrl.href;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedRefiner, selectedCriterion)
            );
        } else {
            currentRefinementCriteria = [];
        }

        return buildListPageUrl(fullUrl, currentRefinementCriteria);
    };

    private _getViewport(): GridSize {
        if (MsDyn365.isBrowser) {
            return isMobile({
                variant: VariantType.Browser,
                context: this.props.context.request
            });
        }
        return isMobile({
            variant: VariantType.Viewport,
            context: this.props.context.request
        });
    }

    private _toggleModal(): void {
        this.setState({
            modalIsOpen: !this.state.modalIsOpen
        });
    }

    private _updateViewport(): void {
        this._viewport = this._getViewport();

        if (this.state.modalIsOpen && !this.isMobile) {
            this._toggleModal();
        }
    }

    private readonly _getexpandRefinerCount = (validRefiners?: number, refinerCount?: expandRefinersCount): void => {
        switch (refinerCount) {
            case expandRefinersCount.all:
                this.expandrefinerCount = validRefiners;
                break;
            case expandRefinersCount.one:
                this.expandrefinerCount = 1;
                break;
            case expandRefinersCount.two:
                this.expandrefinerCount = 2;
                break;
            case expandRefinersCount.three:
                this.expandrefinerCount = 3;
                break;
            case expandRefinersCount.four:
                this.expandrefinerCount = 4;
                break;
            default:
                this.expandrefinerCount = 0;
        }
    };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className='ms-search-result__pagination-left' aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className='ms-search-result__pagination-right' aria-hidden='true' />
            </div>
        );
    };
}
